import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  Box,
  Chip,
  Fade,
  Grow,
} from '@mui/material';
import { ActivityItem } from './components';
import { SportsEsports, Movie, RocketLaunch } from '@mui/icons-material';

interface ActivityItemData {
  id: string;
  text: string;
  icon: React.ReactNode;
  description: string;
  color: string;
}

export const AboutCard = (): React.ReactNode => {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const activities: ActivityItemData[] = [
    {
      id: 'games',
      text: 'Playing Games',
      icon: <SportsEsports />,
      description: '🎮 Level up in life!',
      color: '#c770f0',
    },
    {
      id: 'movies',
      text: 'Watching Horror Movies',
      icon: <Movie />,
      description: '👻 Spooky adventures!',
      color: '#cd5ff8',
    },
    {
      id: 'books',
      text: 'Reading Sci-Fi Books',
      icon: <RocketLaunch />,
      description: '🚀 Exploring new worlds!',
      color: '#be6adf',
    },
  ];

  return (
    <Card
      elevation={3}
      sx={{
        background: 'rgba(24, 26, 39, 0.8)',
        border: '2px solid transparent',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: '0 10px 25px rgba(199, 112, 240, 0.3)',
        },
      }}
    >
      <CardContent>
        <Typography
          variant='body1'
          sx={{
            textAlign: 'justify',
            mb: 3,
            lineHeight: 1.8,
          }}
        >
          Hello everyone! My name is{' '}
          <Typography
            component='span'
            sx={{
              color: 'primary.main',
              fontWeight: 'bold',
            }}
          >
            Islom Mashanlo
          </Typography>{' '}
          and I am from{' '}
          <Typography
            component='span'
            sx={{
              color: 'primary.main',
              fontWeight: 'bold',
            }}
          >
            New York, US.
          </Typography>
          <br />I am an engineer who is currently working as a full stack
          software engineer at{' '}
          <Typography
            component='span'
            sx={{
              color: 'primary.main',
              fontWeight: 'bold',
            }}
          >
            Lifetime Value Co.
          </Typography>
          .
          <br />
          <br />
          Some other activities I love to do, apart from coding!
        </Typography>

        <List sx={{ p: 0 }}>
          {activities.map((activity, index) => (
            <ListItem
              key={activity.id}
              component={ActivityItem}
              onMouseEnter={() => setHoveredItem(activity.id)}
              onMouseLeave={() => setHoveredItem(null)}
              sx={{
                position: 'relative',
                '&:hover::before': {
                  content: `"${activity.description.split(' ')[0]}"`,
                  position: 'absolute',
                  top: '-15px',
                  right: '-15px',
                  fontSize: '2em',
                  zIndex: 1,
                  ...(activity.id === 'games' && {
                    animation: 'gameFloat 2s ease-in-out infinite',
                  }),
                  ...(activity.id === 'movies' && {
                    animation: 'ghostFloat 3s ease-in-out infinite',
                  }),
                  ...(activity.id === 'books' && {
                    animation: 'rocketLaunch 2.5s ease-in-out infinite',
                  }),
                },
                '@keyframes gameFloat': {
                  '0%, 100%': {
                    transform: 'translateY(0) rotate(0deg)',
                  },
                  '50%': {
                    transform: 'translateY(-10px) rotate(5deg)',
                  },
                },
                '@keyframes ghostFloat': {
                  '0%, 100%': {
                    transform: 'translateY(0) scale(1)',
                    opacity: 0.8,
                  },
                  '50%': {
                    transform: 'translateY(-15px) scale(1.1)',
                    opacity: 1,
                  },
                },
                '@keyframes rocketLaunch': {
                  '0%': {
                    transform: 'translateY(0) rotate(0deg)',
                  },
                  '25%': {
                    transform: 'translateY(-5px) rotate(-5deg)',
                  },
                  '50%': {
                    transform: 'translateY(-15px) rotate(0deg)',
                  },
                  '75%': {
                    transform: 'translateY(-25px) rotate(5deg)',
                  },
                  '100%': {
                    transform: 'translateY(-35px) rotate(0deg)',
                    opacity: 0,
                  },
                },
              }}
            >
              <Grow in={true} timeout={500 + index * 200}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
                >
                  <Box
                    sx={{
                      color: activity.color,
                      mr: 2,
                      transition: 'all 0.3s ease',
                      transform:
                        hoveredItem === activity.id
                          ? 'scale(1.2) rotate(10deg)'
                          : 'scale(1)',
                    }}
                  >
                    {activity.icon}
                  </Box>
                  <Typography
                    variant='body1'
                    sx={{
                      flexGrow: 1,
                      fontWeight: 500,
                      transition: 'color 0.3s ease',
                      color:
                        hoveredItem === activity.id
                          ? activity.color
                          : 'text.primary',
                    }}
                  >
                    {activity.text}
                  </Typography>

                  <Fade in={hoveredItem === activity.id}>
                    <Chip
                      label={activity.description}
                      size='small'
                      sx={{
                        backgroundColor: `${activity.color}20`,
                        color: activity.color,
                        border: `1px solid ${activity.color}40`,
                        fontWeight: 500,
                      }}
                    />
                  </Fade>
                </Box>
              </Grow>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};
